import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const COMPANY = {
  name: 'Swiss Chem International',
  address: '82-C Mehar Sadiq Market, Railway Road, Faisalabad',
  phone: '+92 300 7671404',
};

const fmtDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/ /g, '-');
};
const fmtMoney = (n) => 'Rs ' + Math.round(parseFloat(n) || 0).toLocaleString('en-PK');
const fmtQty = (n) => { const v = parseFloat(n) || 0; return v % 1 === 0 ? v.toLocaleString('en-PK') : v.toFixed(2); };

export const generateProductCostLedgerPdf = (ledger, opts = {}) => {
  const { info, opening_qty, closing_qty, entries, totals } = ledger;
  const { from_date, to_date } = opts;

  const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'landscape' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const marginX = 10;
  let y = 12;
  const unit = info.unit || '';
  const rightX = pageWidth - marginX;

  // ─── Header ─────────────────────────────────
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(20, 20, 20);
  doc.text(COMPANY.name, marginX, y);

  doc.setFontSize(17);
  doc.text('Product Cost Ledger', pageWidth / 2, y, { align: 'center' });

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.setTextColor(90, 90, 90);
  doc.text(COMPANY.address, rightX, y - 2, { align: 'right' });
  doc.text(`Ph: ${COMPANY.phone}`, rightX, y + 3, { align: 'right' });
  doc.text(`Printed: ${new Date().toLocaleString('en-GB')}`, rightX, y + 8, { align: 'right' });

  y += 12;
  doc.setDrawColor(160, 160, 160);
  doc.setLineWidth(0.3);
  doc.line(marginX, y, rightX, y);

  // ─── Product info row ────────────────────────
  y += 6;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(26, 60, 110);
  doc.text(`Product: ${info.code} — ${info.name}`, marginX, y);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(40, 40, 40);
  const period = `${from_date ? fmtDate(from_date) : 'All time'} to ${to_date ? fmtDate(to_date) : 'Today'}`;
  doc.text(`Period: ${period}`, pageWidth / 2, y, { align: 'center' });
  doc.text(`Opening Stock: ${fmtQty(opening_qty)} ${unit}`, rightX, y, { align: 'right' });

  y += 5;
  doc.setDrawColor(40, 40, 40);
  doc.setLineWidth(0.5);
  doc.line(marginX, y, rightX, y);
  y += 4;

  // ─── Main table ──────────────────────────────
  const rows = entries.map((e) => {
    if (e.type === 'sale') {
      return [
        fmtDate(e.date),
        'Sale',
        e.invoice_no || '—',
        e.batch_no || '—',
        e.customer_name || '—',
        fmtQty(e.quantity) + ' ' + unit,
        fmtMoney(e.rate),
        fmtMoney(e.line_amount),
        e.commission_agent
          ? `${e.commission_agent}${e.commission_percent != null ? ` (${parseFloat(e.commission_percent)}%)` : ''}`
          : '—',
        e.commission_amount > 0 ? fmtMoney(e.commission_amount) : '—',
        '—',
      ];
    } else {
      return [
        fmtDate(e.date),
        'Expense',
        '—',
        '—',
        e.paid_to || e.description || '—',
        '—',
        '—',
        '—',
        e.expense_type || '—',
        '—',
        fmtMoney(e.amount),
      ];
    }
  });

  autoTable(doc, {
    startY: y,
    head: [['Date', 'Type', 'Invoice', 'Batch', 'Customer / Paid To', 'Qty', 'Rate', 'Sale Amt', 'Agent / Exp.Type', 'Commission', 'Expense']],
    body: rows.length ? rows : [['—', '—', '—', '—', 'No entries', '—', '—', '—', '—', '—', '—']],
    foot: [[
      '', '', '', '', 'TOTALS',
      fmtQty(totals.qty_sold) + ' ' + unit,
      '',
      fmtMoney(totals.revenue),
      '',
      fmtMoney(totals.commission),
      fmtMoney(totals.product_expenses),
    ]],
    theme: 'grid',
    headStyles: { fillColor: [235, 235, 235], textColor: [30, 30, 30], fontStyle: 'bold', fontSize: 7, lineColor: [160, 160, 160], lineWidth: 0.2 },
    bodyStyles: { fontSize: 6.5, textColor: [30, 30, 30], lineColor: [180, 180, 180], lineWidth: 0.15, valign: 'top' },
    footStyles: { fillColor: [245, 245, 245], textColor: [30, 30, 30], fontStyle: 'bold', fontSize: 7, lineColor: [160, 160, 160], lineWidth: 0.2 },
    didParseCell: (data) => {
      if (data.section !== 'body') return;
      const row = entries[data.row.index];
      if (!row) return;
      if (row.type === 'expense') {
        data.cell.styles.fillColor = [255, 253, 245]; // light yellow for expense rows
      }
      if (data.column.index === 7) data.cell.styles.textColor = [26, 60, 110];   // sale amount blue
      if (data.column.index === 9 && data.cell.raw !== '—') data.cell.styles.textColor = [192, 57, 43];   // commission red
      if (data.column.index === 10 && data.cell.raw !== '—') data.cell.styles.textColor = [142, 68, 173]; // expense purple
    },
    columnStyles: {
      0: { cellWidth: 18 },
      1: { cellWidth: 14 },
      2: { cellWidth: 18 },
      3: { cellWidth: 20 },
      4: { cellWidth: 'auto' },
      5: { cellWidth: 18, halign: 'right' },
      6: { cellWidth: 20, halign: 'right' },
      7: { cellWidth: 24, halign: 'right' },
      8: { cellWidth: 28 },
      9: { cellWidth: 22, halign: 'right' },
      10: { cellWidth: 22, halign: 'right' },
    },
    margin: { left: marginX, right: marginX },
  });

  let afterY = doc.lastAutoTable.finalY + 5;

  // ─── Summary box ─────────────────────────────
  doc.setDrawColor(40, 40, 40);
  doc.setLineWidth(0.5);
  doc.line(marginX, afterY, rightX, afterY);
  afterY += 5;

  const summaryItems = [
    { label: 'Total Revenue', value: fmtMoney(totals.revenue), color: [26, 60, 110] },
    { label: 'Less: Commissions', value: `(${fmtMoney(totals.commission)})`, color: [192, 57, 43] },
    { label: 'Less: Product Expenses', value: `(${fmtMoney(totals.product_expenses)})`, color: [142, 68, 173] },
    { label: 'Net Margin', value: fmtMoney(totals.net_margin), color: totals.net_margin >= 0 ? [39, 174, 96] : [192, 57, 43] },
    { label: 'Closing Stock', value: `${fmtQty(closing_qty)} ${unit}`, color: [40, 40, 40] },
  ];

  const colW = (rightX - marginX) / summaryItems.length;
  summaryItems.forEach((s, i) => {
    const sx = marginX + i * colW;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.setTextColor(100, 100, 100);
    doc.text(s.label, sx + colW / 2, afterY, { align: 'center' });
    doc.setFontSize(10);
    doc.setTextColor(...s.color);
    doc.text(s.value, sx + colW / 2, afterY + 6, { align: 'center' });
  });

  // ─── Footer ──────────────────────────────────
  doc.setFontSize(7);
  doc.setTextColor(150, 150, 150);
  doc.text('CONFIDENTIAL — For internal use only', pageWidth / 2, pageHeight - 6, { align: 'center' });
  doc.text('Page 1 of 1', rightX, pageHeight - 6, { align: 'right' });

  doc.save(`ProductCostLedger_${(info.code || 'product').replace(/\s+/g, '_')}.pdf`);
};